import type { DxfLayer } from '../types';
import { buildLayerStrokeColors } from './floor3dLayerColors';
import type { FloorLineBatchResult, ZoneFillResult } from '../workers/scene3dCompute';
import { scene3dWorkerClient } from '../workers/scene3dWorkerClient';
import { aciToHex } from './dxf';

type CacheEntry = {
  key: string;
  lineBatches: FloorLineBatchResult[];
  zoneFills: ZoneFillResult[];
};

let cache: CacheEntry | null = null;
let inFlightKey: string | null = null;
let inFlightPromise: Promise<CacheEntry> | null = null;

/** Bump when 3D floor line styling / elevation changes (invalidates cached batches). */
const FLOOR3D_RENDER_VERSION = 4;

export function floor3dBatchCacheKey(
  polylineCount: number,
  darkMode: boolean
): string {
  return `v${FLOOR3D_RENDER_VERSION}|${polylineCount}|${darkMode}`;
}

export function getCachedFloor3dBatches(key: string): CacheEntry | null {
  return cache?.key === key ? cache : null;
}

export function clearFloor3dBatchCache(): void {
  cache = null;
  inFlightKey = null;
  inFlightPromise = null;
}

export function scheduleFloor3dBatchBuild(args: {
  polylines: number[][];
  polylineLayers: number[];
  polylineEntityColors: number[];
  layers: DxfLayer[];
  visible: boolean;
  darkMode: boolean;
}): Promise<CacheEntry> {
  const key = floor3dBatchCacheKey(args.polylines.length, args.darkMode);

  const hit = getCachedFloor3dBatches(key);
  if (hit) return Promise.resolve(hit);

  if (inFlightPromise && inFlightKey === key) return inFlightPromise;

  inFlightKey = key;
  const layerStrokeColors = buildLayerStrokeColors(args.layers, args.darkMode);
  const polylineStrokeColors = args.polylineLayers.map((layerIndex, polylineIndex) => {
    const entityAci = args.polylineEntityColors[polylineIndex] ?? 256;
    const entityColor = aciToHex(entityAci);
    if (entityColor) return entityColor;
    if (layerIndex >= 0 && layerIndex < layerStrokeColors.length) {
      return layerStrokeColors[layerIndex]!;
    }
    return '#475569';
  });
  inFlightPromise = scene3dWorkerClient
    .computeFloorBatchesIndexed(
      args.polylines,
      args.polylineLayers,
      args.layers.map((l) => l.name),
      layerStrokeColors,
      polylineStrokeColors,
      [], // Always build all layers
      true, // Always visible
      args.darkMode
    )
    .then((result) => {
      const entry: CacheEntry = {
        key,
        lineBatches: result.lineBatches,
        zoneFills: result.zoneFills,
      };
      cache = entry;
      inFlightKey = null;
      inFlightPromise = null;
      return entry;
    })
    .catch((err) => {
      inFlightKey = null;
      inFlightPromise = null;
      throw err;
    });

  return inFlightPromise;
}
